import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { useAuth } from "@/hooks/useAuth";
import { useAssignedPropertyIds } from "@/hooks/useEmployeePropertyAssignments";
import { usePermissions } from "@/hooks/usePermissions";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Database } from "@/integrations/supabase/types";
import { getEffectiveStatus } from "@/lib/contractHelpers";
import { serializeError } from "@/lib/sentry";
import { isValidUUID } from "@/lib/uuid";
import {
  contractsRepository,
  type CreateContractData,
  type CreateContractWithInvoicesData,
  type UpdateContractWithInvoicesData,
  type ContractUnitServiceEntry,
  type ContractUnitPayload,
} from "@/repositories/contracts";

type ContractRow = Database["public"]["Tables"]["contracts"]["Row"];
type ContractStatus = Database["public"]["Enums"]["contract_status"];

export type Contract = ContractRow;

// Re-export the types that external callers (pages, components, tests) import
// from this hook module. Keeping them here preserves the existing import surface.
export type { CreateContractData, CreateContractWithInvoicesData, UpdateContractWithInvoicesData };
export type { ContractUnitServiceEntry, ContractUnitPayload };

// Multi-unit payload types (also used by pages/ContractWizard etc.)
export type { ContractUnitPayload as ContractUnitPayloadExport };

export function useContracts(options?: { enabled?: boolean }) {
  const { companyId } = useAuth();
  const { assignedPropertyIds, isLoading: assignmentsLoading } = useAssignedPropertyIds();
  const { hasPermission } = usePermissions();
  const callerEnabled = options?.enabled ?? true;

  // Layer 1 — permission gate (universal). assignedPropertyIds is null for non-employees.
  const canViewContracts = hasPermission("contracts.view");
  const isEmployee = assignedPropertyIds !== null;

  return useQuery({
    queryKey: ["contracts", companyId, isEmployee ? assignedPropertyIds : "all"],
    queryFn: async () => {
      if (!companyId) return [];
      if (!canViewContracts) return [];
      if (isEmployee && assignedPropertyIds.length === 0) return [];

      return contractsRepository.list({
        companyId,
        assignedPropertyIds: isEmployee ? assignedPropertyIds : null,
      });
    },
    enabled: callerEnabled && !!companyId && !assignmentsLoading,
  });
}

export function useContract(id: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["contract", id],
    queryFn: async () => {
      if (!id || !companyId) return null;
      return contractsRepository.getById(id, companyId);
    },
    // isValidUUID: a malformed URL id (e.g. embedded whitespace) otherwise
    // reaches Postgres as a 22P02 error — Sentry DALLALI-FRONTEND-DZ, #3297.
    enabled: !!id && !!companyId && isValidUUID(id),
  });
}

export function useCreateContract() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (contractData: CreateContractData) => {
      if (!companyId) throw new Error("No company ID");

      // Auto-populate owner_id from property if not provided
      let ownerId = contractData.owner_id;
      if (!ownerId && contractData.property_id) {
        ownerId = (await contractsRepository.getPropertyOwnerId(contractData.property_id, companyId)) ?? undefined;
      }
      // NEW: for multi-owner properties (property.owner_id is null), fall back to unit.owner_id
      if (!ownerId && contractData.unit_id) {
        ownerId = (await contractsRepository.getUnitOwnerId(contractData.unit_id, companyId)) ?? undefined;
      }

      // Generate contract number
      const contractNumber = await contractsRepository.generateContractNumber(companyId);

      // Single repository call: the Nest path owns BOTH the insert and the
      // unit→rented side effect (and the server-side cache invalidation that
      // depends on them); the Supabase fallback reproduces the same pair.
      const data = await contractsRepository.create({
        ...contractData,
        company_id: companyId,
        contract_number: contractNumber,
        contract_type: contractData.contract_type || "rent",
        owner_id: ownerId,
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["contracts-stats"] });
      queryClient.invalidateQueries({ queryKey: ["units"] });
      queryClient.invalidateQueries({ queryKey: ["units-stats"] });
      toast.success(t("تم إنشاء العقد بنجاح", "Contract created successfully"));
    },
    onError: (error) => {
      console.error("Create contract error:", serializeError(error));
      toast.error(t("حدث خطأ أثناء إنشاء العقد", "Error creating contract"));
    },
  });
}

// Multi-unit payload. Each entry corresponds to one unit on the contract.
// service_fees carries up to 8 per-unit annual services — 4 EJAR fees
// (sewage / maintenance / cleaning / security) + 4 utilities (electricity
// / water / gas / internet). Each entry is `{ amount, payer, type? }` so
// the RPC can decide whether to fold the amount into rent invoices (when
// payer = 'tenant') and how to render it. Empty / unused keys are simply
// omitted — the RPC iterates known keys and skips missing ones.

export function useCreateContractWithInvoices() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (contractData: CreateContractWithInvoicesData) => {
      if (!companyId) throw new Error("No company ID");

      // Auto-populate owner_id from property if not provided
      let ownerId = contractData.owner_id || null;
      if (!ownerId && contractData.property_id) {
        ownerId = (await contractsRepository.getPropertyOwnerId(contractData.property_id, companyId)) ?? null;
      }

      // Multi-ownership: fall back to unit.owner_id
      if (!ownerId && contractData.unit_id) {
        ownerId = (await contractsRepository.getUnitOwnerId(contractData.unit_id, companyId)) ?? null;
      }

      return contractsRepository.createWithInvoices(companyId, contractData, ownerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["contracts-stats"] });
      queryClient.invalidateQueries({ queryKey: ["units"] });
      queryClient.invalidateQueries({ queryKey: ["units-stats"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled_payments"] });
      queryClient.invalidateQueries({ queryKey: ["financial-stats"] });
      toast.success(t("تم إنشاء العقد بنجاح", "Contract created successfully"));
    },
    onError: (error) => {
      console.error("Create contract with invoices error:", serializeError(error));
      toast.error(t("حدث خطأ أثناء إنشاء العقد", "Error creating contract"));
    },
  });
}

// Edit flow counterpart to useCreateContractWithInvoices. Calls the
// update_contract_with_invoices RPC which updates the contract row and, when
// a paymentSchedule is supplied, regenerates the unpaid invoices and
// scheduled_payments in the same transaction. Invoices with receipts are
// preserved by the RPC.
export function useUpdateContractWithInvoices() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({ id, paymentSchedule, units, ...rest }: UpdateContractWithInvoicesData) => {
      if (!companyId) throw new Error("No company ID");

      return contractsRepository.updateWithInvoices(id, companyId, {
        ...rest,
        paymentSchedule,
        units,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["contracts-stats"] });
      queryClient.invalidateQueries({ queryKey: ["contract", data?.id] });
      queryClient.invalidateQueries({ queryKey: ["units"] });
      queryClient.invalidateQueries({ queryKey: ["units-stats"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled_payments"] });
      queryClient.invalidateQueries({ queryKey: ["financial-stats"] });
      toast.success(t("تم تحديث العقد بنجاح", "Contract updated successfully"));
    },
    onError: (error: Error) => {
      if (error.message?.includes("Demo account")) return;
      const detail = error.message ? `: ${error.message}` : "";
      toast.error(t("حدث خطأ أثناء تحديث العقد", "Error updating contract") + detail);
    },
  });
}

export function useUpdateContract() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({ id, status, ...rest }: { id: string; status?: ContractStatus } & Partial<Omit<CreateContractData, 'status'>>) => {
      if (!companyId) throw new Error("No company ID");

      const data = await contractsRepository.update(id, companyId, { ...rest, status });

      if (!data) throw new Error("Contract not found or access denied");

      // Sync unit status based on contract status change.
      if (status === "active" && data.unit_id) {
        // Activating a contract → mark unit as rented
        await contractsRepository.markUnitRented(data.unit_id, companyId);
      } else if (status === "terminated" || status === "expired") {
        // Terminating/expiring → revert ALL units on the contract (primary +
        // multi-unit contract_units), each only if no other active contract
        // still holds it. Server-side + atomic; covers multi-unit leases that
        // the old per-`unit_id` client revert left stuck `rented` (C3, #3037).
        // Runs regardless of `data.unit_id` so unit_id-less multi-unit
        // contracts are handled too.
        await contractsRepository.revertUnitsForTerminatedContract(id, companyId);
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["contracts-stats"] });
      queryClient.invalidateQueries({ queryKey: ["contract", data.id] });
      queryClient.invalidateQueries({ queryKey: ["units"] });
      queryClient.invalidateQueries({ queryKey: ["units-stats"] });
      toast.success(t("تم تحديث العقد بنجاح", "Contract updated successfully"));
    },
    onError: (error: Error) => {
      // Demo guard errors are handled by the DemoGuardModal — skip toast
      if (error.message?.includes("Demo account")) return;
      const detail = error.message ? `: ${error.message}` : "";
      toast.error(t("حدث خطأ أثناء تحديث العقد", "Error updating contract") + detail);
    },
  });
}

export function useContractsByTenant(tenantId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["contracts", "tenant", tenantId],
    queryFn: async () => {
      if (!tenantId || !companyId) return [];
      return contractsRepository.listByTenant(tenantId, companyId);
    },
    enabled: !!tenantId && !!companyId,
  });
}

export function useContractsByUnit(unitId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["contracts", "unit", unitId],
    queryFn: async () => {
      if (!unitId || !companyId) return [];
      return contractsRepository.listByUnit(unitId, companyId);
    },
    enabled: !!unitId && !!companyId,
  });
}

export function useContractsStats(range?: { startDate?: string; endDate?: string }) {
  const { companyId } = useAuth();
  const { hasPermission } = usePermissions();

  return useQuery({
    queryKey: ["contracts-stats", companyId, range?.startDate, range?.endDate],
    queryFn: async () => {
      if (!companyId) return { total: 0, active: 0, expired: 0, rent: 0, sale: 0 };
      if (!hasPermission("contracts.view")) return { total: 0, active: 0, expired: 0, rent: 0, sale: 0 };

      const data = await contractsRepository.stats(companyId, range);

      // Use effective status for accurate counting
      return {
        total: data.length,
        active: data.filter(c => getEffectiveStatus({ status: c.status, end_date: c.end_date }) === "active").length,
        expired: data.filter(c => getEffectiveStatus({ status: c.status, end_date: c.end_date }) === "expired").length,
        rent: data.filter(c => c.contract_type === "rent").length,
        sale: data.filter(c => c.contract_type === "sale").length,
      };
    },
    enabled: !!companyId,
  });
}
